
import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { Lead, FunnelStage, Funnel } from '../../types';
import { useOrganization } from '../OrganizationContext';
import { useAuth } from '../AuthContext';
import { useAppContext } from '../AppContext';

import { useFunnelManagement } from './hooks/useFunnelManagement';
import { useStageManagement } from './hooks/useStageManagement';
import { useLeadManagement, type SaveLeadOptions } from './hooks/useLeadManagement';
import { useLeadsPolling } from './hooks/useLeadsPolling';

interface LeadsContextType {
    leads: Lead[];
    funnelStages: FunnelStage[];
    stagesWithCounts: FunnelStage[];
    funnels: Funnel[];
    selectedFunnelId: string | null;
    isLoading: boolean;

    updateLeadStage: (leadId: string, newStageId: string) => Promise<void>;
    saveLead: (leadToSave: Lead, meta?: SaveLeadOptions) => Promise<Lead | null>;
    deleteLead: (leadId: string) => Promise<void>;

    saveFunnelStage: (stageToSave: FunnelStage) => Promise<void>;
    deleteFunnelStage: (stageId: string) => Promise<void>;

    createFunnel: (name: string) => Promise<Funnel | null>;
    updateFunnel: (id: string, name: string) => Promise<void>;
    deleteFunnel: (id: string) => Promise<void>;
    setSelectedFunnelId: (id: string | null) => void;

    reorderStages: (newOrderIds: string[]) => Promise<void>;
    refreshLeads: (funnelIdOverride?: string | null) => Promise<void>;
    refreshFunnels: () => Promise<void>;
    refreshStages: (funnelIdOverride?: string) => Promise<void>;
}

const LeadsContext = createContext<LeadsContextType | undefined>(undefined);

/** Única fonte: etapas + leads do funil, ou visão org-wide quando não há funil. */
async function syncFunnelScope(opts: {
    funnelId: string | null;
    refreshStages: (funnelIdOverride?: string) => Promise<void>;
    refreshLeads: (funnelIdOverride?: string | null) => Promise<void>;
    onEmptyFunnel?: () => void;
}) {
    const { funnelId, refreshStages, refreshLeads, onEmptyFunnel } = opts;
    if (funnelId) {
        await Promise.all([refreshStages(funnelId), refreshLeads(funnelId)]);
        return;
    }
    onEmptyFunnel?.();
    await refreshLeads(null);
}

export const LeadsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { currentOrganization } = useOrganization();
    const { user } = useAuth();
    const { currentPage } = useAppContext();
    const [isLoading, setIsLoading] = useState(true);

    /**
     * Dashboard: visão agregada da org (RLS limita por papel).
     * Fora do dashboard (Kanban etc.): etapas e negociações seguem o funil selecionado.
     */
    const fetchAllLeads = currentPage === 'dashboard';

    const {
        funnels,
        setFunnels,
        selectedFunnelId,
        setSelectedFunnelId,
        refreshFunnels,
        createFunnel,
        updateFunnel,
        deleteFunnel,
    } = useFunnelManagement(currentOrganization?.id);

    const {
        funnelStages,
        setFunnelStages,
        refreshStages,
        saveFunnelStage,
        deleteFunnelStage,
        reorderStages,
    } = useStageManagement(selectedFunnelId, currentOrganization?.id);

    const {
        leads,
        setLeads,
        refreshLeads,
        saveLead,
        deleteLead,
        updateLeadStage,
    } = useLeadManagement(currentOrganization?.id, selectedFunnelId, funnelStages, fetchAllLeads);

    const stagesWithCounts = useMemo(
        () =>
            funnelStages.map((stage) => ({
                ...stage,
                leadCount: leads.filter((lead) => lead.stage === stage.id).length,
            })),
        [funnelStages, leads]
    );

    useLeadsPolling({
        onRefresh: refreshLeads,
        organizationId: currentOrganization?.id,
        selectedFunnelId,
        fetchAllLeads,
        enabled: !!currentOrganization,
    });

    // Bootstrap org + alternância dashboard ↔ funil (via fetchAllLeads)
    useEffect(() => {
        if (!currentOrganization) {
            setIsLoading(false);
            setLeads([]);
            setFunnels([]);
            setFunnelStages([]);
            return;
        }

        const loadData = async () => {
            setIsLoading(true);
            try {
                const tasks: Promise<void>[] = [refreshFunnels()];
                if (fetchAllLeads) {
                    tasks.push(refreshLeads(null));
                } else {
                    tasks.push(
                        syncFunnelScope({
                            funnelId: selectedFunnelId,
                            refreshStages,
                            refreshLeads,
                        })
                    );
                }
                await Promise.all(tasks);
            } catch (error) {
                console.error('Failed to load initial data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        void loadData();
        // user/role: troca de conta na mesma org precisa reconsultar sob o JWT/RLS novos
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentOrganization?.id, currentOrganization?.role, user?.id, fetchAllLeads]);

    // Troca de funil no Kanban (não depende de fetchAllLeads — evita reload duplo ao sair do dashboard)
    useEffect(() => {
        if (fetchAllLeads || !currentOrganization) return;

        void syncFunnelScope({
            funnelId: selectedFunnelId,
            refreshStages,
            refreshLeads,
            onEmptyFunnel: () => setFunnelStages([]),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFunnelId]);

    useEffect(() => {
        const handleAssignmentChange = () => {
            refreshLeads();
        };
        window.addEventListener('assignment:changed', handleAssignmentChange);
        return () => window.removeEventListener('assignment:changed', handleAssignmentChange);
    }, [refreshLeads]);

    const value = useMemo((): LeadsContextType => ({
        leads, funnelStages, stagesWithCounts, funnels, selectedFunnelId, isLoading,
        updateLeadStage, saveLead, deleteLead,
        saveFunnelStage, deleteFunnelStage,
        createFunnel, updateFunnel, deleteFunnel, setSelectedFunnelId,
        reorderStages, refreshLeads, refreshFunnels, refreshStages,
    }), [
        leads, funnelStages, stagesWithCounts, funnels, selectedFunnelId, isLoading,
        updateLeadStage, saveLead, deleteLead, saveFunnelStage, deleteFunnelStage,
        createFunnel, updateFunnel, deleteFunnel, setSelectedFunnelId,
        reorderStages, refreshLeads, refreshFunnels, refreshStages,
    ]);

    return <LeadsContext.Provider value={value}>{children}</LeadsContext.Provider>;
};

export const useLeads = (): LeadsContextType => {
    const context = useContext(LeadsContext);
    if (!context) {
        throw new Error('useLeads must be used within a LeadsProvider');
    }
    return context;
};
